// @ts-strict

import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  OnDestroy,
  HostListener
} from '@angular/core';
import { SwiperOptions } from 'swiper/types/swiper-options';
import Swiper from 'swiper/types/swiper-class';
import { Router } from '@angular/router';

interface ItemBase {
  name: string;
  active: boolean;
  icon?: string;
  faIcon?: string;
  disabled?: boolean;
  count?: number;
  loading?: boolean;
  error?: boolean;
  id?: number;
  link?: string;
}

interface TranslatedItem extends ItemBase {
  translation: string;
  text?: never;
}

interface TextItem extends ItemBase {
  translation?: never;
  text: string;
}

export type ScrollableTabMenuItem = TranslatedItem | TextItem;

@Component({
  selector: 'app-scrollable-tab-menu',
  templateUrl: './scrollable-tab-menu.component.html',
  styleUrls: ['./scrollable-tab-menu.component.scss']
})
export class ScrollableTabMenuComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @ViewChild('swiperRef') swiperRef!: ElementRef;

  @Input() menuItems: ScrollableTabMenuItem[] = [];
  @Input() withBorderBottom = false;
  @Input() uniqueId = '';
  @Input() options: SwiperOptions = {};
  @Input() checkActiveRoute = false;

  @Output() getActiveMenuItem = new EventEmitter<ScrollableTabMenuItem>();

  public activeMenuItem: ScrollableTabMenuItem | undefined;
  public isMobile = false;

  public config: SwiperOptions = {
    slidesPerView: 'auto',
    grabCursor: true,
    freeMode: true,
    initialSlide: 0,
    ...this.options
  };

  private _swiper?: Swiper;

  constructor(private router: Router) {}

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.checkScreenSize();
  }

  ngOnInit(): void {
    this.checkScreenSize();

    if (this.checkActiveRoute) {
      this.activeMenuItem = this.menuItems.find(item => this.router.url.includes(item.link as string));
    } else {
      this.activeMenuItem = this.menuItems.find(item => item.active);
    }

    this.setActiveItemFromLocalStorage();
  }

  ngAfterViewInit() {
    if (!this.isMobile) {
      this._swiper = this.swiperRef?.nativeElement.swiper;
      this.scrollToActiveMenu();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.activeMenuItem = this.menuItems.find(item => item.active);

    this.watchChanges(changes);

    if (!this.isMobile) {
      this.scrollToActiveMenu();
    }
  }

  ngOnDestroy(): void {
    // Cleanup if needed
  }

  public disable = (): void => {
    this.menuItems = this.menuItems.map(item => ({ ...item, disabled: true }));
  };

  public enable = (): void => {
    this.menuItems = this.menuItems.map(item => ({ ...item, disabled: false }));
  };

  public handleMenuItemClick = (menuItem: ScrollableTabMenuItem) => {
    this.activeMenuItem = this.menuItems.find(item => item.name === menuItem.name);

    if (this.activeMenuItem) {
      this.getActiveMenuItem.emit(this.activeMenuItem);
    }

    if (this.uniqueId && this.activeMenuItem) {
      localStorage.setItem(this.uniqueId, this.activeMenuItem.name);
    }
  };

  private checkScreenSize(): void {
    this.isMobile = window.innerWidth < 1200;
  }

  private scrollToActiveMenu() {
    const activeMenuItemId = this.menuItems.findIndex(({ name }) => this.activeMenuItem?.name === name);

    if (activeMenuItemId > -1 && this._swiper) this._swiper.slideTo(activeMenuItemId);
  }

  private setActiveItemFromLocalStorage = (): void => {
    if (!this.uniqueId) return;

    const activeElementName = localStorage.getItem(this.uniqueId);

    if (!activeElementName) return;

    this.activeMenuItem = this.menuItems.find(({ name }) => name === activeElementName);
  };

  private watchChanges = (changes: SimpleChanges) => {
    if (changes.menuItems && !changes.menuItems.firstChange) {
      const hasChanged =
        this.menuItems.filter((item, index) => {
          const previousItem = changes.menuItems.previousValue[index];

          return item?.loading !== previousItem?.loading;
        }).length > 0;

      if (hasChanged && !this.isMobile) {
        this._swiper?.update();
      }
    }
  };
}
